import { Alert, Button, Checkbox, Form, Input, InputNumber } from "antd";
import { FormattedMessage } from "react-intl";
import TimePickerField from "components/fields/TimePickerField";
import EventDescriptionField from "views/Events/EventForm/Tabs/EventInfo/EventDescriptionField";
import { validateHtmlHeight } from "utils/htmlHeightValidator";

import { TicketPoolFormValue, TicketPoolFormProps } from "./types";
import { ticketPoolFormLayout } from "./consts";
import { useTicketAvailabilityDates } from "./hooks";
import * as Styled from "./styles";

const TicketPoolForm: React.FC<TicketPoolFormProps> = ({
  loading,
  eventDate,
  form,
  asSignUpOptions,
  ...props
}) => {
  const { availableBeforeDate, expireBeforeDate } = useTicketAvailabilityDates(
    eventDate,
    form
  );

  const canEditPrice = !form?.getFieldValue("hasAnyTicketSold");

  return (
    <Form<TicketPoolFormValue>
      {...ticketPoolFormLayout}
      {...{ form, ...props }}
    >
      <Form.Item
        label={<FormattedMessage defaultMessage="Name" />}
        name="name"
        required
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={<FormattedMessage defaultMessage="QTY" />}
        name="quantity"
        required
      >
        <InputNumber precision={0} />
      </Form.Item>
      {asSignUpOptions ? (
        <>
          <Form.Item
            label={<FormattedMessage defaultMessage="Max Tickets Per Order" />}
            name="maxTicketsPerOrder"
          >
            <InputNumber precision={0} />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Min Tickets Per Order" />}
            name="minTicketsPerOrder"
          >
            <InputNumber precision={0} />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Scanner Only" />}
            name="isScannerOnly"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Auto Approve" />}
            name="isAutoApprove"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item
            label={<FormattedMessage defaultMessage="Face Value Price" />}
            name="price"
            required
          >
            <InputNumber precision={2} disabled={!canEditPrice} />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Venue Fee" />}
            name="venueFee"
            required
          >
            <InputNumber precision={2} disabled={!canEditPrice} />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Max Tickets Per Order" />}
            name="maxTicketsPerOrder"
          >
            <InputNumber precision={0} />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Min Tickets Per Order" />}
            name="minTicketsPerOrder"
          >
            <InputNumber precision={0} />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="On Sale Date" />}
            extra={
              <FormattedMessage defaultMessage="Number of Days Before Event" />
            }
            name="availableBeforeDays"
          >
            <InputNumber precision={0} />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="On Sale Time" />}
            name="availableBeforeTime"
            extra={
              availableBeforeDate && (
                <Styled.TicketAvailabilityInfoWrapper>
                  <Alert
                    type="info"
                    message={
                      <FormattedMessage
                        defaultMessage="Sale will start at {startsAt}"
                        values={{ startsAt: availableBeforeDate }}
                      />
                    }
                  />
                </Styled.TicketAvailabilityInfoWrapper>
              )
            }
          >
            <TimePickerField />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Expiration Date" />}
            extra={
              <FormattedMessage defaultMessage="Number of Days Before Event" />
            }
            name="expireBeforeDays"
          >
            <InputNumber precision={0} />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Expiration Time" />}
            name="expireBeforeTime"
            extra={
              expireBeforeDate && (
                <Styled.TicketAvailabilityInfoWrapper>
                  <Alert
                    type="info"
                    message={
                      <FormattedMessage
                        defaultMessage="Tickets will expire at {expiresAt}"
                        values={{ expiresAt: expireBeforeDate }}
                      />
                    }
                  />
                </Styled.TicketAvailabilityInfoWrapper>
              )
            }
          >
            <TimePickerField />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Ticket Description" />}
            name="description"
            rules={[validateHtmlHeight(120)]}
          >
            <EventDescriptionField />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage defaultMessage="Scanner Only" />}
            name="isScannerOnly"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
        </>
      )}
      <Form.Item>
        <Button type="primary" htmlType="submit" {...{ loading }}>
          <FormattedMessage defaultMessage="Submit" />
        </Button>
      </Form.Item>
    </Form>
  );
};

export default TicketPoolForm;
